// Stripe.js singleton. loadStripe returns a Promise the <Elements> provider
// consumes; it must be created once at module scope (not per render) so Stripe
// isn't re-initialized on every mount.
//
// The publishable key (pk_test_… / pk_live_…) is client-safe by design — it only
// tokenizes cards; the secret key lives solely in valoi-billing. Supplied via
// VITE_STRIPE_PUBLISHABLE_KEY (gitignored .env.development.local in dev; real
// deploy env in prod). When it's missing we resolve to null so the module never
// throws on import — WalletPage renders a "billing unavailable" state instead.
import { loadStripe, type Stripe } from '@stripe/stripe-js';

const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;

// A NON-EMPTY placeholder is worse than an empty one, which is why this checks the
// shape and not just the presence. `.env.production` shipped `pk_live_xxx` as a
// "fill this in" marker and the staging build never overrode it, so `Boolean(...)`
// was true, the card form rendered as though billing worked, and the failure only
// surfaced when the user pressed Save — as Stripe's own "Invalid API Key provided:
// pk_live_xxx". Every staging card save failed that way, and nothing on the wallet
// page hinted the build was misconfigured rather than the service down.
//
// Deliberately a prefix + length test rather than a full-format regex: real keys are
// ~100+ chars of base62 whose exact alphabet is Stripe's to change, and a strict
// pattern that rejects a VALID key would take card payments down for the opposite
// reason. Any placeholder short enough to type by hand fails this; a real key passes
// regardless of what Stripe does to the tail.
const MIN_PUBLISHABLE_KEY_LENGTH = 20;

export function isUsablePublishableKey(key: string | undefined): key is string {
  const trimmed = key?.trim();
  if (!trimmed) return false;
  if (!trimmed.startsWith('pk_test_') && !trimmed.startsWith('pk_live_')) return false;
  return trimmed.length >= MIN_PUBLISHABLE_KEY_LENGTH;
}

// Resolved once, into a single value both exports read. Keeping `hasStripeKey` as an
// independent Boolean(...) of the raw variable is what let the two disagree: the flag
// said "we have a key" while the thing handed to loadStripe was a placeholder.
const usableKey: string | null = isUsablePublishableKey(publishableKey)
  ? publishableKey.trim()
  : null;

export const hasStripeKey = usableKey !== null;

export const stripePromise: Promise<Stripe | null> = usableKey
  ? loadStripe(usableKey)
  : Promise.resolve(null);
